import type { MetaFunction } from "react-router";
import { marketingLinks, MarketingShell, socialImageMeta } from "../components/marketing/layout";
import {
  ArrowRight,
  FileText,
  SendLink,
  Handshake,
  Receipt,
  Scan,
  Layers,
  Sparkles,
  Scale,
  Buildings,
  Download,
  ShieldCheck,
} from "../components/marketing/icons";
import { SITE_ORIGIN } from "../brand";

const APP_STORE_URL = "https://apps.shopify.com/quote-desk";
const PAGE_URL = `${SITE_ORIGIN}/features`;

export const links = marketingLinks;

export const meta: MetaFunction = () => [
  { title: "Quote Desk Features for Shopify B2B Quotes" },
  {
    name: "description",
    content:
      "Manage negotiated quotes, buyer links, draft-order acceptance, PO PDF parsing, counter-offers, company records, and bulk operations in Shopify.",
  },
  {
    name: "keywords",
    content:
      "Shopify B2B quote workflow, PO PDF parser Shopify, AI counter-offer engine, draftOrderCreate B2B, Shopify wholesale without Plus",
  },
  { property: "og:title", content: "AI B2B Quote Desk — Full Feature Breakdown" },
  {
    property: "og:description",
    content: "12 capabilities across Quote workflow / PO parsing / AI negotiation / Bulk ops. The B2B workflow Shopify normally gates behind Plus.",
  },
  { property: "og:type", content: "website" },
  { property: "og:url", content: PAGE_URL },
  ...socialImageMeta(),
  { tagName: "link", rel: "canonical", href: PAGE_URL },
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What does AI B2B Quote Desk actually do?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Three things: (1) builds professional B2B quotes from your Shopify catalog with negotiated per-quote pricing; (2) publishes public quote links (/q/<token>) the customer accepts in one click → real Shopify Order via the draftOrderCreate GraphQL mutation; (3) on Pro+ tier, parses incoming PO PDFs into draft quotes automatically AND runs an AI counter-offer engine that suggests reasonable price moves during negotiations.",
          },
        },
        {
          "@type": "Question",
          name: "How is this different from Shopify Plus B2B?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Shopify Plus B2B has negotiated pricing, draft orders, and quote links built into the platform. It also costs $2,300/month minimum. Quote Desk delivers the same outcomes (negotiated per-quote pricing, quote links, draft-to-order flow) for Shopify Basic, Grow, and Advanced merchants at $29-79/month. We don't replace Plus's broader checkout customization or wholesale storefront features — we focus on the quote workflow specifically and price accordingly.",
          },
        },
        {
          "@type": "Question",
          name: "How does PO PDF parsing work?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "On Pro+ tier, drop a PO PDF into the dashboard. We extract line items (SKU, description, quantity, requested price), shipping address, billing address, customer name, requested delivery date. The output pre-fills a draft quote that you review before sending. Average parsing time: 8-12 seconds. Average accuracy on standard PO templates: 92-97%. Manual review takes about 30-60 seconds before sending. Net result: a 15-minute PO-by-email becomes a 60-90 second flow.",
          },
        },
        {
          "@type": "Question",
          name: "What's the AI counter-offer engine?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "When a customer responds to your quote with a counter-offer (e.g., 'can you do $4,200 instead of $4,500?'), the engine analyzes your historical pricing for this customer + your margin floor + typical negotiation patterns in your category, and suggests a reasonable counter-counter (e.g., '$4,350 with 30-day payment terms'). You approve, edit, or override before sending. The engine never sends on your behalf — it's a suggestion layer, not an autonomous agent.",
          },
        },
        {
          "@type": "Question",
          name: "What happens when the customer accepts a quote?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "The customer hits the public quote link (/q/<token>) and clicks Accept. We call Shopify's draftOrderCreate GraphQL mutation to create a real Shopify Order from the quote line items. The customer then pays through your normal Shopify checkout flow — credit card, ACH, or any payment method you have enabled. Order data flows into your Shopify admin like any other order.",
          },
        },
        {
          "@type": "Question",
          name: "Do you store customer payment information?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "No. Payment is handled by Shopify's checkout, not Quote Desk. We never see, store, or transmit credit card numbers. The negotiated per-quote pricing we store are SKU/discount tuples — not financial credentials. The quote tokens (/q/<token>) are random opaque strings; they don't reveal customer identity or order value to anyone who doesn't have the URL.",
          },
        },
      ],
    },
  },
];

interface Feature {
  category: string;
  icon: React.ReactNode;
  title: string;
  body: string;
  example: string;
  tier?: "Starter" | "Pro" | "Growth";
}

const FEATURES: Feature[] = [
  {
    category: "Quote workflow",
    icon: <Scale />,
    title: "Negotiated per-quote pricing",
    body: "Set the unit price on each line as you build a quote — per-SKU, per-quantity, per-deal. The line totals and the quote total recompute as you negotiate.",
    example: "Acme asks for 500 boxes. Drop SS-1000 from $1.40 to $1.09 on the line, watch the quote total update, send.",
  },
  {
    category: "Quote workflow",
    icon: <SendLink />,
    title: "Public quote links",
    body: "Every quote gets a public URL at /q/<random-token>. Share it by email, Slack, or any channel — the buyer doesn't need a Shopify account. Tokens are opaque so they don't leak order value or buyer identity.",
    example: "Send 'Here's your quote: …/q/abc123xyz789' — the buyer clicks, reviews, accepts.",
  },
  {
    category: "Quote workflow",
    icon: <Handshake />,
    title: "draftOrderCreate accept flow",
    body: "When the buyer clicks Accept, we call Shopify's draftOrderCreate GraphQL mutation to create a real Shopify order. They pay through your normal Shopify checkout — credit card, ACH, anything you accept.",
    example: "Buyer accepts → real Shopify Order #1234 → appears in Orders → fulfilled like any normal order.",
  },
  {
    category: "Quote workflow",
    icon: <FileText />,
    title: "Branded quote PDFs",
    body: "Generate a clean, branded PDF for any quote — your logo and brand color — to attach in email or hand to a buyer who wants a document. Included on every plan, free too.",
    example: "A buyer's procurement team needs a PDF on file. Open the quote → download the branded PDF → attach it to your reply.",
  },
  {
    category: "Quote workflow",
    icon: <FileText />,
    title: "Quote ledger & reversibility",
    body: "Every quote action — create, send, view, accept, decline, cancel — is logged with timestamps. A full audit trail for disputes, and cancelled quotes can be re-sent without rebuilding.",
    example: "A buyer disputes a line six weeks later. Open the ledger → the exact prices they accepted, with the timestamp.",
  },
  {
    category: "PO parsing",
    icon: <Scan />,
    title: "PO PDF → draft order",
    body: "Drop a PO PDF. We extract line items, quantities, prices, and buyer info, then match each line to your catalog with a confidence score. Pre-fills a draft quote ready for review.",
    example: "A buyer emails a PO PDF. Drop it in → line items extracted and matched → review the low-confidence rows → send.",
    tier: "Pro",
  },
  {
    category: "PO parsing",
    icon: <Layers />,
    title: "Confidence-scored matching",
    body: "Exact-SKU match first, then case-insensitive, then a fuzzy match on the description. Anything below the confidence threshold is flagged for you to confirm — nothing silently becomes a wrong line.",
    example: "SKU 'SS1000' on the PO matches your 'SS-1000' at high confidence; a vague 'box, large' is flagged for review.",
    tier: "Pro",
  },
  {
    category: "AI negotiation",
    icon: <Sparkles />,
    title: "Counter-offer engine",
    body: "When a buyer counters, the engine reads that company's order history, your margin floor, and typical category patterns, and drafts a reasonable counter-counter. You approve, edit, or override before sending.",
    example: "Buyer counters $4,200. Engine drafts '$4,350 with 30-day terms' from Acme's prior $4,400 deal and your margin floor.",
    tier: "Growth",
  },
  {
    category: "AI negotiation",
    icon: <Scale />,
    title: "Margin-aware, never autonomous",
    body: "Set your margin floor and the engine never drafts below it. It's a suggestion layer with a per-line rationale — it never sends on your behalf. You keep final control of every price.",
    example: "Margin floor 30% on SS-1000. A buyer offer that would breach it gets a 'hold' recommendation, not a discount.",
    tier: "Growth",
  },
  {
    category: "Company management",
    icon: <Buildings />,
    title: "Repeat B2B customer profiles",
    body: "Track companies with multiple buyers, locations, and quote history — pulled from your Shopify companies. Build a quote for any contact and their company context comes with it.",
    example: "Acme has four buyers across two ship-to locations. Build a quote for Buyer Sue → Acme's history and locations are right there.",
  },
  {
    category: "Bulk operations",
    icon: <Layers />,
    title: "CSV bulk import",
    body: "Import line items from a CSV to build big quotes fast instead of adding products one at a time. Ideal for long catalogs and recurring order templates.",
    example: "A 60-line reorder arrives as a spreadsheet. Import the CSV → a draft quote in seconds → adjust prices → send.",
    tier: "Starter",
  },
  {
    category: "Bulk operations",
    icon: <Download />,
    title: "Filtered CSV export",
    body: "Export quotes to CSV — filtered by status, company, or date — for finance and reconciliation in your accounting tool.",
    example: "Month-end close: export all accepted quotes for Q3 → reconcile against Shopify orders in your accounting tool.",
    tier: "Starter",
  },
  {
    category: "Confidence",
    icon: <ShieldCheck />,
    title: "Verification harness",
    body: "Replay your current pricing rules against historical orders and see how many would have produced identical totals — proof there's no regression before you roll out a pricing change.",
    example: "Before changing your discount rules, replay them across the last 200 orders and confirm the totals still line up.",
    tier: "Growth",
  },
];

const CATEGORIES = [
  "Quote workflow",
  "PO parsing",
  "AI negotiation",
  "Company management",
  "Bulk operations",
  "Confidence",
];

const ACCEPT_STEPS = [
  "You build a B2B quote in the dashboard — line items with negotiated per-quote pricing.",
  "You click Send. Quote Desk generates a public quote link (/q/<random-token>).",
  "You share the link by email, Slack, or any channel.",
  "The buyer opens the link in a browser — no Shopify account required.",
  "The buyer clicks Accept on the public quote page.",
  "Quote Desk calls Shopify's draftOrderCreate mutation to create a real order.",
  "The buyer pays through your normal Shopify checkout.",
  "The order flows into your Shopify admin like any other order. Fulfill as usual.",
];

export default function FeaturesPage() {
  return (
    <MarketingShell>
      <section className="qd-hero" style={{ paddingBottom: "1rem" }}>
        <div className="qd-wrap">
          <div className="qd-head qd-head--center qd-reveal" data-d="1">
            <p className="qd-eyebrow qd-eyebrow--center">Feature breakdown</p>
            <h1 style={{ fontSize: "var(--step-4)", marginTop: "1rem" }}>
              The whole B2B quote workflow, in one app
            </h1>
            <p>
              Negotiated per-quote pricing, public quote links, the
              draftOrderCreate accept flow, PO PDF parsing, AI counter-offers,
              company management, and bulk operations — for Shopify Basic, Grow,
              and Advanced. No Plus required.
            </p>
            <p style={{ marginTop: "0.9rem", color: "var(--ink-500)", fontSize: "0.9rem" }}>
              Everything without a plan badge is included on the <strong>Free</strong> plan.
              Badges mark where a feature starts: Starter, Pro, or Growth.
            </p>
          </div>
          <div
            className="qd-hero__cta"
            style={{ justifyContent: "center", marginTop: "1.8rem" }}
          >
            <a className="qd-btn" href={APP_STORE_URL}>
              See it on the App Store
              <ArrowRight />
            </a>
            <a className="qd-btn qd-btn--ghost" href="/pricing">
              See pricing
            </a>
          </div>
        </div>
      </section>

      {CATEGORIES.map((cat, ci) => {
        const items = FEATURES.filter((f) => f.category === cat);
        const banded = ci % 2 === 1;
        return (
          <section
            key={cat}
            className={`qd-section--tight ${banded ? "qd-band" : ""}`}
          >
            <div className="qd-wrap">
              <div className="qd-head qd-reveal" data-d="1">
                <p className="qd-eyebrow">{cat}</p>
              </div>
              <div className="qd-cards" style={{ marginTop: "1.4rem" }}>
                {items.map((f) => (
                  <article className="qd-card" key={f.title}>
                    {f.tier ? (
                      <span
                        className={`qd-card__tier qd-badge ${
                          f.tier === "Growth"
                            ? "qd-badge--growth"
                            : f.tier === "Starter"
                              ? "qd-badge--starter"
                              : "qd-badge--pro"
                        }`}
                      >
                        {f.tier}
                      </span>
                    ) : null}
                    <span className="qd-card__icon">{f.icon}</span>
                    <h3>{f.title}</h3>
                    <p>{f.body}</p>
                    <p className="qd-card__eg">
                      <b>Example</b>
                      {f.example}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      <section className="qd-section">
        <div className="qd-wrap qd-wrap--narrow">
          <div className="qd-head qd-reveal" data-d="1">
            <p className="qd-eyebrow">The accept loop</p>
            <h2>How a quote becomes a paid order</h2>
          </div>
          <ol
            className="qd-prose"
            style={{ marginTop: "2rem", paddingLeft: "1.3rem", display: "grid", gap: "0.7rem" }}
          >
            {ACCEPT_STEPS.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
          <div className="qd-callout">
            <p>
              <strong>Reversible by design.</strong> The buyer can decline, and
              you can cancel any quote until Accept is clicked. Every action is
              logged to the quote ledger for a full audit trail.
            </p>
          </div>
        </div>
      </section>

      <section className="qd-section qd-band">
        <div className="qd-wrap">
          <div className="qd-cta qd-reveal" data-d="1">
            <h2>See every feature on the live app</h2>
            <p>
              Quote Desk is available on the Shopify App Store now. Start a
              14-day free trial on any plan.
            </p>
            <div className="qd-cta__actions">
              <a className="qd-btn" href={APP_STORE_URL}>
                Visit the App Store listing
                <ArrowRight />
              </a>
              <a className="qd-btn qd-btn--ghost" href="/compare">
                Compare with Shopify Plus
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
